import {
  DEFAULT_CSS_SELECTOR_ALLOWLIST,
  type BeforePlugin,
  type ElementInteractionsOptions,
  type EnrichmentPlugin,
  type Event as AmplitudeEvent,
} from '@amplitude/analytics-browser/lib/esm/types';

import {
  AMPLITUDE_PRODUCT_AREA_ALLOWLISTS,
  getAmplitudePageUrlAllowlistForProductAreaIds,
} from 'analytics/amplitudePageUrlAllowlist';
import BenchlingConfig from 'BenchlingConfig';
import pick from 'util/pick';

const PAGE_URL_PROPERTY_KEYS = [
  '[Amplitude] Page URL',
  '[Amplitude] Page Path',
  '[Amplitude] Page Location',
  '[Amplitude] Previous Page Location',
];

/**
 * Amplitude stores matching data-test-* attrs under this property, with the
 * `data-test-` prefix stripped from each key (see DataTestAttrs field names).
 */
const ELEMENT_ATTRIBUTES_PROPERTY_KEY = '[Amplitude] Element Attributes';

/** Keep stable UI selectors; drop `key` and ad-hoc attrs (often customer text/IDs/timestamps). */
const ALLOWED_ELEMENT_ATTRIBUTE_KEYS = ['component', 'element', 'index'];

/** Extra Element Attributes keys sent when the target node has `data-amp-safe`. */
const AMP_SAFE_ELEMENT_ATTRIBUTE_KEYS = ['key'];

/**
 * Put this on an element whose `data-test-key` values are not customer data / PII.
 * The privacy plugin then sends `key` in addition to `component`, `element`, and `index`.
 *
 * Not an Amplitude SDK flag — only this plugin reads it. Autocapture does not
 * copy it into `[Amplitude] Element Attributes` (those are `data-test-*` only);
 * the plugin looks for it on the target node in `[Amplitude] Element Hierarchy`
 * and then drops it from the payload.
 *
 * @example
 * <button {...getTestProps({component: 'Save', element: 'root', key: 'status'})} data-amp-safe={true} />
 */
export const AMP_SAFE_ATTR = 'data-amp-safe';

/** DOM ancestry of the interacted element, from the element itself up to (excluding) `<html>`. */
const ELEMENT_HIERARCHY_PROPERTY_KEY = '[Amplitude] Element Hierarchy';

/**
 * Structural keys kept on each hierarchy node. `id` and `classes` are omitted, matching
 * the dropped `[Amplitude] Element ID` / `Class` / `Path` event properties.
 */
const ALLOWED_ELEMENT_HIERARCHY_NODE_KEYS = ['tag', 'index', 'indexOfType', 'prevSib'];

/**
 * Hierarchy `attrs` keys are full attribute names, unlike the prefix-stripped
 * ELEMENT_ATTRIBUTES_PROPERTY_KEY map. Reuse the same data-test-* allowlist, plus `type`.
 * Every other attr (title, placeholder, aria-*, ad-hoc data-*) can carry customer text.
 * `data-amp-safe` is read then dropped; it is never sent.
 */
const ALLOWED_ELEMENT_HIERARCHY_ATTRIBUTE_KEYS = [
  'type',
  ...ALLOWED_ELEMENT_ATTRIBUTE_KEYS.map((key) => `data-test-${key}`),
];

const AMP_SAFE_ELEMENT_HIERARCHY_ATTRIBUTE_KEYS = AMP_SAFE_ELEMENT_ATTRIBUTE_KEYS.map(
  (key) => `data-test-${key}`
);

/**
 * Known Amplitude autocapture / page-view keys we keep. New keys are omitted until
 * they are added here. Custom precision-tracking properties are not sent via this SDK.
 */
const ALLOWED_EVENT_PROPERTY_KEYS = [
  ...PAGE_URL_PROPERTY_KEYS,
  ELEMENT_ATTRIBUTES_PROPERTY_KEY,
  ELEMENT_HIERARCHY_PROPERTY_KEY,
  '[Amplitude] Page Domain',
  '[Amplitude] Previous Page Type',
  '[Amplitude] Page Counter',
  '[Amplitude] Page View ID',
  '[Amplitude] Element Tag',
  '[Amplitude] Element Position Left',
  '[Amplitude] Element Position Top',
  '[Amplitude] Viewport Height',
  '[Amplitude] Viewport Width',
];

export function getAmplitudeElementInteractionsOptions(): ElementInteractionsOptions {
  const options: ElementInteractionsOptions = {
    // Capture data-test-* attrs; privacy plugin keeps component/element/index, and also
    // key when `data-amp-safe` is set on the element.
    dataAttributePrefix: 'data-test-',
    visualTaggingOptions: {
      enabled: true,
    },
    viewportContentUpdated: {
      enabled: false,
    },
    cssSelectorAllowlist: ['[tabindex="0"]', ...DEFAULT_CSS_SELECTOR_ALLOWLIST],
  };
  if (!BenchlingConfig.ANALYTICS_DISABLE_AUTOCAPTURE_PAGE_URL_ALLOWLIST) {
    options.pageUrlAllowlist = getAmplitudePageUrlAllowlistForProductAreaIds(
      BenchlingConfig.ANALYTICS_AUTOCAPTURE_PAGE_URL_ALLOWLIST_PRODUCT_AREAS,
      AMPLITUDE_PRODUCT_AREA_ALLOWLISTS
    );
  }
  return options;
}

/** React `data-amp-safe={true}` stringifies to `"true"` in the DOM. */
function isAmpSafe(attrs: unknown): boolean {
  if (attrs == null || typeof attrs !== 'object') {
    return false;
  }
  return (attrs as Record<string, unknown>)[AMP_SAFE_ATTR] === 'true';
}

function filterElementAttributes(sanitized: Record<string, unknown>): void {
  const attributes = sanitized[ELEMENT_ATTRIBUTES_PROPERTY_KEY];
  if (attributes == null) {
    return;
  }
  const hierarchy = sanitized[ELEMENT_HIERARCHY_PROPERTY_KEY];
  let extraKeys: Array<string> = [];
  if (hierarchy != null) {
    const target = (hierarchy as Array<Record<string, unknown>>)[0];
    if (isAmpSafe(target?.attrs)) {
      extraKeys = AMP_SAFE_ELEMENT_ATTRIBUTE_KEYS;
    }
  }
  const filtered = pick(
    attributes as Record<string, unknown>,
    ...ALLOWED_ELEMENT_ATTRIBUTE_KEYS,
    ...extraKeys
  );
  if (Object.keys(filtered).length === 0) {
    delete sanitized[ELEMENT_ATTRIBUTES_PROPERTY_KEY];
    return;
  }
  sanitized[ELEMENT_ATTRIBUTES_PROPERTY_KEY] = filtered;
}

function filterElementHierarchyNode(node: Record<string, unknown>): Record<string, unknown> {
  const filtered = pick(node, ...ALLOWED_ELEMENT_HIERARCHY_NODE_KEYS);
  if (node.attrs != null) {
    const attrs = node.attrs as Record<string, unknown>;
    const filteredAttrs = pick(
      attrs,
      ...ALLOWED_ELEMENT_HIERARCHY_ATTRIBUTE_KEYS,
      ...(isAmpSafe(attrs) ? AMP_SAFE_ELEMENT_HIERARCHY_ATTRIBUTE_KEYS : [])
    );
    if (Object.keys(filteredAttrs).length > 0) {
      filtered.attrs = filteredAttrs;
    }
  }
  return filtered;
}

function filterElementHierarchy(sanitized: Record<string, unknown>): void {
  const hierarchy = sanitized[ELEMENT_HIERARCHY_PROPERTY_KEY];
  if (hierarchy == null) {
    return;
  }
  sanitized[ELEMENT_HIERARCHY_PROPERTY_KEY] = (hierarchy as Array<Record<string, unknown>>).map((node) =>
    filterElementHierarchyNode(node)
  );
}

function sanitizeProperties(properties: Record<string, unknown>): Record<string, unknown> {
  let sanitized: Record<string, unknown>;
  if (BenchlingConfig.ANALYTICS_DISABLE_AUTOCAPTURE_PROPERTY_ALLOWLIST) {
    sanitized = {...properties};
  } else {
    sanitized = pick(properties, ...ALLOWED_EVENT_PROPERTY_KEYS);
    filterElementAttributes(sanitized);
    filterElementHierarchy(sanitized);
  }
  return sanitized;
}

export interface AmplitudePrivacyReadyLatch {
  waitUntilReady: () => Promise<void>;
  markReady: () => void;
}

export function createAmplitudePrivacyReadyLatch(): AmplitudePrivacyReadyLatch {
  let ready = false;
  let resolvers: Array<() => void> = [];
  return {
    waitUntilReady: (): Promise<void> => {
      if (ready) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        resolvers.push(resolve);
      });
    },
    markReady: (): void => {
      ready = true;
      for (const resolve of resolvers) {
        resolve();
      }
      resolvers = [];
    },
  };
}

/**
 * Before-phase plugin that holds events until the privacy enrichment plugin is installed.
 * Performs no redaction — enrichment owns property filtering.
 */
export function createAmplitudePrivacyBeforePlugin(waitUntilReady: () => Promise<void>): BeforePlugin {
  return {
    name: 'benchling-amplitude-privacy-before',
    type: 'before',
    execute: async (event: AmplitudeEvent): Promise<AmplitudeEvent | null> => {
      await waitUntilReady();
      return event;
    },
  };
}

/** Enrichment plugin that allowlists autocapture properties. */
export function createAmplitudePrivacyEnrichmentPlugin(): EnrichmentPlugin {
  return {
    name: 'benchling-amplitude-privacy',
    type: 'enrichment',
    execute: (event: AmplitudeEvent): Promise<AmplitudeEvent | null> => {
      // Identify / setGroup use user_properties and groups, not event_properties.
      if (event.event_properties == null) {
        return Promise.resolve(event);
      }
      // Event includes RevenueEvent, whose event_properties aren't freely string-indexable.
      return Promise.resolve({
        ...event,
        event_properties: sanitizeProperties(event.event_properties as Record<string, unknown>),
      });
    },
  };
}
